<template>
  <!-- HERO Section: Professional, conversion-focused -->
  <section class="min-h-[400px] bg-gray-100 pt-8 md:pt-16 pb-16 px-4">
    <div class="max-w-7xl mx-auto space-y-6">
      <div class="text-center mb-8">
        <h1 class="text-4xl md:text-5xl font-extrabold tracking-tight mb-4 leading-tight">
          AI YouTube Video Idea Generator
        </h1>
        <div style="display: none;" class="text-lg md:text-xl font-semibold text-gray-700 mb-2">
          (Powered by Real Trending Videos)
        </div>
        <p class="text-lg md:text-xl text-gray-700/90 max-w-3xl mx-auto mb-6 font-medium">
          Backed by real YouTube trending videos —
          <span class="text-primary-dark font-semibold">every idea includes video examples to help you create better content.</span>
        </p>
        <!-- Trust signals -->
        <div style="display: none;" class="flex flex-wrap justify-center gap-6 mb-8 text-sm">
          <div class="flex items-center gap-2 text-gray-600">
            <span class="material-symbols-outlined text-primary">verified</span>
            Powered by YouTube trend data
          </div>
          <div class="flex items-center gap-2 text-gray-600">
            <span class="material-symbols-outlined text-primary">bolt</span>
            AI-powered recommendations
          </div>
          <div class="flex items-center gap-2 text-gray-600">
            <span class="material-symbols-outlined text-primary">schedule</span>
            Save hours of research
          </div>
        </div>
      </div>

      <!-- CTA: Compact search bar -->
      <div class="w-full max-w-3xl mx-auto">
        <div class="flex flex-col sm:flex-row items-stretch rounded-2xl shadow-sm ring-1 ring-black/10 bg-white/95 backdrop-blur px-2 py-2 gap-2 border border-gray-200">
          <div class="flex-1 flex flex-col sm:flex-row items-center rounded-xl bg-white h-auto sm:h-14 px-4 overflow-hidden gap-2">
            <SearchForm
              class="w-full [&_*]:!mb-0 [&_*]:!shadow-none [&_*]:!ring-0 [&_*]:!border-0 [&_*]:!bg-transparent"
              :regionCode="regionCode"
              :regions="regions"
              :categoryId="categoryId"
              :categories="categories"
              :loading="loading"
              @regionChange="$emit('regionChange', $event)"
              @categoryChange="$emit('categoryChange', $event)"
              @reset="$emit('reset')"
            />
          </div>
          <!-- Center the button on mobile -->
          <div class="flex justify-center items-center w-full sm:w-auto">
            <button
              @click="$emit('search')"
              :disabled="loading"
              class="relative h-14 md:w-72 rounded-xl font-semibold text-white shadow-md ring-1 ring-black/10
                     focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-70
                     disabled:cursor-not-allowed overflow-hidden transition-all duration-150
                     bg-gradient-to-r from-primary to-primary-dark w-full"
              aria-live="polite"
            >
              <!-- SOLID TINT FILL: starts at 0% width on the far left, grows to the right -->
              <span
                class="absolute inset-y-0 left-0 pointer-events-none transition-[width] duration-150 ease-linear"
                :style="{
                  width: loading ? (Math.max(0, Math.min(100, progress))) + '%' : '0%',
                  background: 'rgba(0, 0, 0, 0.25)'
                }"
                aria-hidden="true"
              ></span>

              <!-- CONTENT -->
              <span class="relative z-10 flex items-center justify-center gap-2 h-full">
                <span class="material-symbols-outlined text-2xl"
                      :class="loading ? 'animate-spin' : ''">
                  {{ loading ? 'autorenew' : (clickCount > 1 ? 'refresh' : 'bolt') }}
                </span>

                <span v-if="!loading">
                  {{ clickCount > 1 ? 'Generate Fresh Ideas' : 'Generate Ideas' }}
                </span>
                <span v-else>
                  <span v-if="analyzingPhase">
                    {{ clickCount > 1 ? 'Getting fresh trends…' : 'Analyzing trends…' }}
                  </span>
                  <span v-else>Generating…</span>
                </span>
              </span>
            </button>
          </div>
        </div>

        <!-- Secondary CTA -->
        <div class="mt-4 text-sm text-gray-500 text-center">
          <span class="material-symbols-outlined text-primary align-middle">info</span>
          {{ clickCount > 1 ? 'Getting you fresh ideas!' : 'No credit card required. Free to try!' }}
        </div>

        <!-- Social Proof -->
        <div class="mt-6" style="display: none;">
          <SocialProof />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import SearchForm from '../../components/trends/SearchForm.vue';
import SocialProof from './SocialProof.vue';

export default {
  name: 'Hero_',
  components: { SearchForm, SocialProof },
  props: {
    regionCode: { type: String, required: true },
    regions: { type: Object, required: true },
    categoryId: { type: String, required: true },
    categories: { type: Object, required: true },
    loading: { type: Boolean, required: true },
    analyzingPhase: { type: Boolean, required: true },
    clickCount: { type: Number, default: 0 }
  },
  emits: ['search', 'regionChange', 'categoryChange', 'reset'],
  data() {
    return {
      progress: 0,             // 0..100 – drives the tint width
      rafId: null,             // requestAnimationFrame id
      startTs: 0,              // animation start timestamp
      lastTs: 0,               // last frame timestamp
      minFillMs: 3200,         // time to reach 90% (linear): tweak to taste
      creepPercentPerSec: 1.2, // creep speed after 90% (percent per second)
      creepMax: 97             // never exceed until real completion
    };
  },
  watch: {
    loading: {
      immediate: true,
      handler(isLoading) {
        if (isLoading) {
          this.progress = 0;   // start at 0 for immediate left-start effect
          this.startProgress();
        } else {
          this.stopProgress();
          this.progress = 100; // finish visibly
          setTimeout(() => { this.progress = 0; }, 250);
        }
      }
    }
  },
  methods: {
    startProgress() {
      this.stopProgress();
      this.startTs = performance.now();
      this.lastTs = this.startTs;

      const step = (ts) => {
        // bail if loading ended mid-frame
        if (!this.loading) return;

        const elapsed = ts - this.startTs;
        const dt = ts - this.lastTs;
        this.lastTs = ts;

        // Linear portion: 0 -> 90 over minFillMs
        const linearPortion = Math.min(90, (elapsed / this.minFillMs) * 90);

        if (linearPortion < 90) {
          this.progress = linearPortion;
        } else {
          // Creep portion: slowly inch toward creepMax while still loading
          const creepPerMs = this.creepPercentPerSec / 1000; // % per ms
          this.progress = Math.min(this.creepMax, this.progress + creepPerMs * dt);
        }

        this.rafId = requestAnimationFrame(step);
      };

      this.rafId = requestAnimationFrame(step);
    },
    stopProgress() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }
  },
  beforeUnmount() {
    this.stopProgress();
  }
};
</script>

<style scoped>
.shadow-sm {
  box-shadow: 0 2px 8px 0 rgba(80,36,255,0.06);
}
.shadow-md {
  box-shadow: 0 4px 16px 0 rgba(80,36,255,0.08);
}
.bg-gray-100 {
  --tw-bg-opacity: 1;
  background-color: rgb(243 244 246 / var(--tw-bg-opacity));
}
.border-gray-200 {
  border-color: rgb(229 231 235);
}
</style>
